import React, { useCallback, useEffect, useState } from 'react';
import styles from 'components/podiatry/page.module.scss';
import { StateType } from 'reducer';
import {
  ActiveTypeOfReasonForVisit,
  usePersistRFVReducer,
} from 'reducer/reasonForVisit';
import { actionTypesOfReasonForVisit } from 'reducer/actionType';
import { appStateSettingsAction } from 'reduxStateSlice/appState';
import { useDispatch } from 'react-redux';
import { PodiatryConditionalOptionsType } from 'types/types';
import MetaTag from 'components/meta';
import InputText from 'components/common/InputText';
import Button from 'components/common/button';
import { useScrollIntoView } from 'hooks/useScroll';
import clsx from 'clsx';

type Props = {
  options: PodiatryConditionalOptionsType[] | PodiatryConditionalOptionsType;
  nextPage: number;
  currentPage?: number;
  actionPage: string;
  multiple?: boolean;
  isFilter?: boolean;
  required?: boolean;
  textFieldForOther?: boolean;
  nextOrSubmitButton: (nextPage: number, submit?: boolean) => void;
};

function CommonPage({
  options,
  nextPage,
  nextOrSubmitButton,
  actionPage,
  multiple = true,
  required = true,
  isFilter = false,
  textFieldForOther = false,
}: Props) {
  const { errorTagRefs, scrollToError } = useScrollIntoView();
  const [inputText, setInputText] = useState('');
  const [inputTextError, setInputTextError] = useState(false);
  const [title, setTitle] = useState('');
  const [subTitle, setSubTitle] = useState<string | null>('');
  const [questionOptions, setQuestionOptions] = useState<string[]>([]);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [error, setError] = useState<boolean>(false);

  const [state, dispatch] = usePersistRFVReducer();
  const ReduxDispatch = useDispatch();

  useEffect(() => {
    if (isFilter && Array.isArray(options)) {
      const findOptions = options.find(
        (obj: PodiatryConditionalOptionsType) => obj.name === actionPage,
      );
      if (findOptions) {
        try {
          setQuestionOptions(findOptions.options);
          setTitle(findOptions.title);
          setSubTitle(findOptions.subTitle);
        } catch (error) {
          console.error('🚀 ~ Error on filter options', error);
        }
      }
    } else if (!Array.isArray(options)) {
      setQuestionOptions(options.options);
      setTitle(options.title);
      setSubTitle(options.subTitle);
    }
  }, []);

  useEffect(() => {
    if (state) {
      const data = state.find((object: StateType) => object.name === actionPage);
      if (data) {
        setSelectedItems(data.value);
        if (data.text) setInputText(data.text);
      }
      if (actionTypesOfReasonForVisit.REASON_FOR_VISIT === actionPage) {
        ReduxDispatch(
          appStateSettingsAction({ reasonForVisit: data.value.toString() }),
        );
      }
    }
  }, []);

  const onSelectOptions = useCallback((item: string) => {
    const preventItems = ['None', 'No major life changes', 'Not applicable'];
    if (multiple) {
      if (preventItems.includes(item)) {
        setSelectedItems([item]);
      } else {
        setSelectedItems((old) => {
          if (preventItems.includes(old.toString())) {
            return [item];
          } if (old.find((value) => value === item)) {
            return old.filter((data) => data !== item);
          } return [...old, item];
        });
      }
    } else {
      setSelectedItems([item]);
    }

    setError(false);
  }, []);

  const clickNextOrSubmit = async (nextPage: number) => {
    if (required && selectedItems.length === 0) {
      setError(true);
      scrollToError();
      return;
    }

    const other = selectedItems.find((item) => item === 'Other');
    if (other && textFieldForOther) {
      if (!inputText.trim()) {
        setInputTextError(true);
        return;
      }
    }

    const dispatchPayload: ActiveTypeOfReasonForVisit = {
      type: actionPage,
      name: title,
      value: selectedItems,
    };
    if (inputText && other) {
      dispatchPayload.text = inputText.trim();
    }

    if (actionTypesOfReasonForVisit.REASON_FOR_VISIT === actionPage) {
      ReduxDispatch(
        appStateSettingsAction({ reasonForVisit: selectedItems.toString() }),
      );
    }
    await dispatch(dispatchPayload);
    nextOrSubmitButton(nextPage);
  };

  const onChangeText = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputText(event.target.value);
    setInputTextError(false);
  };

  return (
    <>
      <MetaTag title={title} />
      <div ref={errorTagRefs[0]}>
        <h2 className={`marginTopZero ${styles.titleText}`}>{title}</h2>
        {subTitle && <p className={styles.titleTextSubText}>{subTitle}</p>}
        {error && (
        <p className="error-message">
          Please select at least one option.
        </p>
        )}
        <div>
          {questionOptions.map((option) => {
            const selectedClass = selectedItems.find(
              (item) => item === option,
            );
            return (
              <div key={option}>
                <p
                  className={clsx('option', { 'selected-option': selectedClass })}
                  onClick={() => onSelectOptions(option)}
                  onKeyUp={() => {}}
                >
                  {option}
                </p>
                {textFieldForOther && option === 'Other' && selectedClass && (
                <InputText
                  value={inputText}
                  placeholder="Type..."
                  onChange={onChangeText}
                  error={inputTextError}
                  maxLength={160}
                  isWidthFull
                />
                )}
              </div>
            );
          })}
        </div>
      </div>
      <div className="next-button-fixed">
        <Button onClick={() => clickNextOrSubmit(nextPage)}>
          {nextPage === 15 ? 'Submit' : 'Next'}
        </Button>
      </div>
    </>
  );
}

export default CommonPage;
